/**
 * Classic tie-breaker distilled from Grok decisions already on the desk.
 * No extra model call. The policy holds counts and codes, never prompts,
 * mail, prices, or a Hoppie logon.
 */
import { matchDeclineReason } from "./declineReason";
import type { ExperienceRecord } from "./experienceLog";
import type { OpsDeclineReason } from "./types";

export const LEARNED_POLICY_KIND = "northstar.dispatch.learned-policy" as const;
export const LEARNED_POLICY_VERSION = 1;
export const LEARNED_SUPPORT_MIN = 5;
export const LEARNED_HALF_LIFE_MS = 30 * 24 * 60 * 60 * 1000;
export const LEARNED_DECLINE_PROPENSITY = 0.2;
export const LEARNED_DISTILL_GAP_MS = 60 * 60 * 1000;

export type LearnedBucket = {
  flightType: string;
  distanceBand: string;
  paxBand: string;
  hourBand: string;
  icaoType: string;
  keptWeight: number;
  decisionWeight: number;
  support: number;
  propensity: number;
};

export type LearnedTail = { originIcao: string; distanceBand: string; aircraftId: string; kept: number };
export type LearnedCrew = { originIcao: string; distanceBand: string; crewId: string; kept: number };
export type LearnedSlip = { clashKind: string; medianSlipMin: number; support: number };

export type LearnedPolicy = {
  kind: typeof LEARNED_POLICY_KIND;
  version: typeof LEARNED_POLICY_VERSION;
  distilledAtUtc: string;
  grokDecisions: number;
  buckets: LearnedBucket[];
  tails: LearnedTail[];
  crews: LearnedCrew[];
  slips: LearnedSlip[];
};

const SECRET = /xai-|bearer\s|logon=|sk-live|@[a-z0-9.-]+\.[a-z]{2,}/i;

export function emptyLearnedPolicy(now = new Date()): LearnedPolicy {
  return {
    kind: LEARNED_POLICY_KIND,
    version: LEARNED_POLICY_VERSION,
    distilledAtUtc: now.toISOString(),
    grokDecisions: 0,
    buckets: [],
    tails: [],
    crews: [],
    slips: [],
  };
}

export function containsBlockedSecret(value: unknown): boolean {
  return SECRET.test(JSON.stringify(value ?? ""));
}

function num(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function token(raw: unknown, max: number): string {
  return String(raw ?? "").trim().slice(0, max);
}

export function hydrateLearnedPolicy(raw: unknown): LearnedPolicy | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Partial<LearnedPolicy>;
  if (src.kind !== LEARNED_POLICY_KIND || src.version !== LEARNED_POLICY_VERSION) return null;
  const buckets = Array.isArray(src.buckets)
    ? src.buckets
        .map((row) => {
          const item = row as Partial<LearnedBucket>;
          const support = Math.max(0, Math.round(num(item.support)));
          const decisionWeight = Math.max(0, num(item.decisionWeight));
          const keptWeight = Math.max(0, num(item.keptWeight));
          return {
            flightType: token(item.flightType, 24),
            distanceBand: token(item.distanceBand, 16),
            paxBand: token(item.paxBand, 8),
            hourBand: token(item.hourBand, 8),
            icaoType: token(item.icaoType, 8).toUpperCase(),
            keptWeight,
            decisionWeight,
            support,
            propensity: decisionWeight + 2 === 0 ? 0.5 : (keptWeight + 1) / (decisionWeight + 2),
          };
        })
        .filter((row) => row.support > 0)
    : [];
  const tails = Array.isArray(src.tails)
    ? src.tails.map((row) => {
        const item = row as Partial<LearnedTail>;
        return {
          originIcao: token(item.originIcao, 4).toUpperCase(),
          distanceBand: token(item.distanceBand, 16),
          aircraftId: token(item.aircraftId, 64),
          kept: Math.max(0, Math.round(num(item.kept))),
        };
      }).filter((row) => row.aircraftId && row.kept > 0)
    : [];
  const crews = Array.isArray(src.crews)
    ? src.crews.map((row) => {
        const item = row as Partial<LearnedCrew>;
        return {
          originIcao: token(item.originIcao, 4).toUpperCase(),
          distanceBand: token(item.distanceBand, 16),
          crewId: token(item.crewId, 64),
          kept: Math.max(0, Math.round(num(item.kept))),
        };
      }).filter((row) => row.crewId && row.kept > 0)
    : [];
  const slips = Array.isArray(src.slips)
    ? src.slips.map((row) => {
        const item = row as Partial<LearnedSlip>;
        return {
          clashKind: token(item.clashKind, 32),
          medianSlipMin: Math.round(num(item.medianSlipMin)),
          support: Math.max(0, Math.round(num(item.support))),
        };
      }).filter((row) => row.clashKind && row.support > 0)
    : [];
  const policy: LearnedPolicy = {
    kind: LEARNED_POLICY_KIND,
    version: LEARNED_POLICY_VERSION,
    distilledAtUtc: token(src.distilledAtUtc, 40) || new Date(0).toISOString(),
    grokDecisions: Math.max(0, Math.round(num(src.grokDecisions))),
    buckets,
    tails,
    crews,
    slips,
  };
  return containsBlockedSecret(policy) ? emptyLearnedPolicy(new Date(policy.distilledAtUtc)) : policy;
}

function decay(atUtc: string, now: Date): number {
  const at = Date.parse(atUtc);
  if (!Number.isFinite(at)) return 0;
  const age = Math.max(0, now.getTime() - at);
  return Math.pow(0.5, age / LEARNED_HALF_LIFE_MS);
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2) return sorted[mid]!;
  return Math.round((sorted[mid - 1]! + sorted[mid]!) / 2);
}

function bucketKey(row: Pick<LearnedBucket, "flightType" | "distanceBand" | "paxBand" | "hourBand" | "icaoType">): string {
  return [row.flightType, row.distanceBand, row.paxBand, row.hourBand, row.icaoType].join("|");
}

type DecisionRow = ExperienceRecord & {
  distanceBand?: string;
  slipMin?: number;
  clashKind?: string;
  chosenCrewId?: string;
  reasonCode?: string;
  shadow?: boolean;
};

function isGrokDecision(row: DecisionRow): boolean {
  if (row.shadow) return false;
  if (row.context?.actor !== "grok") return false;
  return row.action === "accept" || row.action === "accept_assign" || row.action === "decline" || row.action === "reschedule" || row.action === "reassign_tail" || row.action === "reassign_crew" || row.action === "flag_review";
}

function isKeptAccept(row: DecisionRow): boolean {
  if (row.action !== "accept" && row.action !== "accept_assign") return false;
  return row.outcome === "kept" || row.outcome.startsWith("kept");
}

/** Laplace propensity. Buckets under the support floor stay in the file and are ignored by Classic. */
export function distillPolicy(log: readonly ExperienceRecord[] | null | undefined, now = new Date()): LearnedPolicy {
  const buckets = new Map<string, LearnedBucket & { key: string }>();
  const tails = new Map<string, LearnedTail>();
  const crews = new Map<string, LearnedCrew>();
  const slips = new Map<string, number[]>();
  let grokDecisions = 0;
  for (const raw of log ?? []) {
    const row = raw as DecisionRow;
    if (!isGrokDecision(row)) continue;
    grokDecisions += 1;
    const weight = decay(row.atUtc, now);
    const features = row.features;
    const key = bucketKey({
      flightType: features.flightType,
      distanceBand: row.distanceBand ?? "",
      paxBand: features.paxBand,
      hourBand: features.hourBand,
      icaoType: features.icaoType,
    });
    const bucket = buckets.get(key) ?? {
      key,
      flightType: features.flightType,
      distanceBand: row.distanceBand ?? "",
      paxBand: features.paxBand,
      hourBand: features.hourBand,
      icaoType: features.icaoType,
      keptWeight: 0,
      decisionWeight: 0,
      support: 0,
      propensity: 0.5,
    };
    bucket.support += 1;
    bucket.decisionWeight += weight;
    if (isKeptAccept(row)) bucket.keptWeight += weight;
    buckets.set(key, bucket);
    if (isKeptAccept(row) && features.aircraftId) {
      const origin = features.originIcao;
      const tailKey = `${origin}|${row.distanceBand ?? ""}|${features.aircraftId}`;
      const tail = tails.get(tailKey) ?? { originIcao: origin, distanceBand: row.distanceBand ?? "", aircraftId: features.aircraftId, kept: 0 };
      tail.kept += 1;
      tails.set(tailKey, tail);
      if (row.chosenCrewId) {
        const crewKey = `${origin}|${row.distanceBand ?? ""}|${row.chosenCrewId}`;
        const crew = crews.get(crewKey) ?? { originIcao: origin, distanceBand: row.distanceBand ?? "", crewId: row.chosenCrewId, kept: 0 };
        crew.kept += 1;
        crews.set(crewKey, crew);
      }
    }
    if (row.action === "reschedule" && row.outcome === "kept" && row.clashKind && typeof row.slipMin === "number") {
      const list = slips.get(row.clashKind) ?? [];
      list.push(row.slipMin);
      slips.set(row.clashKind, list);
    }
  }
  const outBuckets = [...buckets.values()]
    .map((row) => ({
      flightType: row.flightType,
      distanceBand: row.distanceBand,
      paxBand: row.paxBand,
      hourBand: row.hourBand,
      icaoType: row.icaoType,
      keptWeight: round4(row.keptWeight),
      decisionWeight: round4(row.decisionWeight),
      support: row.support,
      propensity: round4((row.keptWeight + 1) / (row.decisionWeight + 2)),
    }))
    .sort((a, b) => bucketKey(a).localeCompare(bucketKey(b)));
  const policy: LearnedPolicy = {
    kind: LEARNED_POLICY_KIND,
    version: LEARNED_POLICY_VERSION,
    distilledAtUtc: now.toISOString(),
    grokDecisions,
    buckets: outBuckets,
    tails: [...tails.values()].sort((a, b) => b.kept - a.kept || a.aircraftId.localeCompare(b.aircraftId)),
    crews: [...crews.values()].sort((a, b) => b.kept - a.kept || a.crewId.localeCompare(b.crewId)),
    slips: [...slips.entries()]
      .map(([clashKind, values]) => ({ clashKind, medianSlipMin: median(values), support: values.length }))
      .sort((a, b) => a.clashKind.localeCompare(b.clashKind)),
  };
  return containsBlockedSecret(policy) ? emptyLearnedPolicy(now) : policy;
}

function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

export function learnedAcceptGate(
  policy: LearnedPolicy | null | undefined,
  probe: { flightType: string; distanceBand: string; paxBand: string; hourBand: string; icaoType: string },
): { hold: boolean; propensity: number; support: number } {
  if (!policy) return { hold: false, propensity: 0.5, support: 0 };
  const hit = policy.buckets.find(
    (row) =>
      row.flightType === probe.flightType &&
      row.distanceBand === probe.distanceBand &&
      row.paxBand === probe.paxBand &&
      row.hourBand === probe.hourBand &&
      row.icaoType === probe.icaoType,
  );
  if (!hit || hit.support < LEARNED_SUPPORT_MIN) return { hold: false, propensity: hit?.propensity ?? 0.5, support: hit?.support ?? 0 };
  return { hold: hit.propensity < LEARNED_DECLINE_PROPENSITY, propensity: hit.propensity, support: hit.support };
}

/** Higher is a stronger kept habit. Zero means no usable preference. */
export function learnedPairRank(
  policy: LearnedPolicy | null | undefined,
  aircraftId: string,
  crewId: string,
  originIcao: string,
  distanceBand: string,
): number {
  if (!policy) return 0;
  const origin = originIcao.toUpperCase();
  const tail = policy.tails.find((row) => row.aircraftId === aircraftId && row.originIcao === origin && row.distanceBand === distanceBand);
  const crew = policy.crews.find((row) => row.crewId === crewId && row.originIcao === origin && row.distanceBand === distanceBand);
  const kept = (tail?.kept ?? 0) + (crew?.kept ?? 0);
  return kept >= LEARNED_SUPPORT_MIN ? kept : 0;
}

export function learnedSlipMinutes(policy: LearnedPolicy | null | undefined, clashKind?: string): number | null {
  if (!policy?.slips.length) return null;
  const rows = policy.slips.filter((row) => row.support >= LEARNED_SUPPORT_MIN && (!clashKind || row.clashKind === clashKind));
  if (!rows.length) return null;
  const best = [...rows].sort((a, b) => b.support - a.support || a.clashKind.localeCompare(b.clashKind))[0]!;
  return best.medianSlipMin > 0 ? best.medianSlipMin : null;
}

export function distillDue(lastAt: string | null | undefined, now: Date): boolean {
  if (!lastAt) return true;
  const at = Date.parse(lastAt);
  if (!Number.isFinite(at)) return true;
  return now.getTime() - at >= LEARNED_DISTILL_GAP_MS;
}

const REASON_IDS = new Set(["schedule", "crew", "aircraft", "weather", "price", "other"]);

export function compactReasonCode(reasons: readonly OpsDeclineReason[] | null | undefined, raw: unknown): string {
  const matched = matchDeclineReason(reasons, raw);
  if (matched && REASON_IDS.has(matched)) return matched;
  if (matched) return matched.slice(0, 32);
  const text = String(raw ?? "").toLowerCase();
  if (!text.trim()) return "";
  if (/\bweather|metar|taf|\bwx\b/.test(text)) return "weather";
  if (/\bcrew|duty|fdp|rest\b/.test(text)) return "crew";
  if (/\baircraft|tail|mel|squawk|maintenance\b/.test(text)) return "aircraft";
  if (/\bprice|cost|rate|quote\b/.test(text)) return "price";
  if (/\bschedule|overlap|clash|time|std\b/.test(text)) return "schedule";
  return "other";
}
